import React, { useState, useEffect, useCallback } from 'react';
import { Toolbox, Phone, MapPin, FileText, ChevronDown } from 'lucide-react';
import SegmentedControl from '../shared/SegmentedControl';
import RotationPicker from './shared/RotationPicker';
import RotationNotes from './shared/RotationNotes';
import HospitalHeader, { hospitalHex } from './shared/HospitalHeader';
import OverviewTab from './shared/OverviewTab';
import ContactList from './shared/ContactList';
import LocationList from './shared/LocationList';
import DocumentList from './shared/DocumentList';
import GuideScreen from './shared/GuideScreen';
import ToolScreen from './shared/ToolScreen';
import { rotationsFor, resolveRotation, menuLabelFor } from './registry';
import { useScrollToTop } from '../../hooks/useScrollReset';
import { useBackHandler } from '../../hooks/NavigationContext';
import { notify } from '../../utils/notify';

/*
 * One screen for every hospital. What it shows comes from registry.js:
 * which rotations exist, their data files, and which full-screen tools they
 * can open. The screen itself knows nothing hospital-specific.
 */

const TAB_DEFS = [
  { id: 'overview', label: 'Tools', icon: Toolbox },
  { id: 'contacts', label: 'Contacts', icon: Phone },
  { id: 'locations', label: 'Locations', icon: MapPin },
  { id: 'documents', label: 'Documents', icon: FileText },
];

// Entry points from elsewhere in the app, mapped to tool ids
const INITIAL_ACTIONS = {
  showCaseSetupGuide: ['case-setup'],
  showOOOR: ['epic', 'ooor'],
  showPreopChecklist: ['preop'],
};

const HospitalDetailScreen = ({ hospital, onBack, initialAction }) => {
  const rotations = rotationsFor(hospital.id);
  const [activeRotation, setActiveRotation] = useState(rotations[0]?.id || null);
  const [activeTab, setActiveTab] = useState('overview');
  const [tool, setTool] = useState(null); // { id, arg } while a full-screen tool is open
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    setActiveRotation(rotationsFor(hospital.id)[0]?.id || null);
    setActiveTab('overview');
    setTool(null);
    setPickerOpen(false);
  }, [hospital.id]);

  useEffect(() => {
    const target = INITIAL_ACTIONS[initialAction];
    if (target) setTool({ id: target[0], arg: target[1] });
  }, [initialAction]);

  useScrollToTop([activeTab, activeRotation, tool]);

  // Back closes the open tool or the picker before it leaves the hospital
  const closeTool = useCallback(() => setTool(null), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  useBackHandler(Boolean(tool), closeTool);
  useBackHandler(pickerOpen, closePicker);

  const rotation = resolveRotation(hospital.id, activeRotation);
  const accent = hospitalHex(hospital);

  // ---- tools ---------------------------------------------------------------
  // Guides declared in the rotation's data (`guides: { id: guide }`) open as `guide:<id>`
  const guideTools = Object.fromEntries(
    Object.entries(rotation?.data?.guides || {}).map(([id, guide]) => [`guide:${id}`, {
      render: (ctx) => (
        <ToolScreen hospital={ctx.hospital} subtitle={guide.title} onBack={ctx.close}>
          <GuideScreen guide={guide} accent={rotation?.data?.accent || accent} />
        </ToolScreen>
      ),
    }])
  );
  const tools = {
    notes: {
      render: () => <RotationNotes hospital={hospital} rotation={rotation} onBack={() => setTool(null)} />,
    },
    ...guideTools,
    ...(rotation?.tools || {}),
  };
  const open = (id, arg) => {
    const def = tools[id];
    if (!def) { notify.info('Coming soon'); return; }
    if (def.alias) { open(def.alias[0], def.alias[1]); return; }
    setTool({ id, arg });
  };
  const runAction = (id) => open(id);

  if (tool && tools[tool.id]?.render) {
    return tools[tool.id].render({
      hospital,
      rotation,
      arg: tool.arg,
      open,
      close: () => setTool(null),
    });
  }

  // ---- rotation switcher ---------------------------------------------------
  // Rotations with `menu: true` share one segment that opens a picker.
  const primary = rotations.filter((r) => !r.menu);
  const menuRotations = rotations.filter((r) => r.menu);
  const activeInMenu = menuRotations.find((r) => r.id === activeRotation);
  const MENU_ID = '__menu';
  const segments = [
    ...primary.map((r) => ({ id: r.id, label: r.label })),
    ...(menuRotations.length ? [{ id: MENU_ID, label: activeInMenu ? activeInMenu.label : menuLabelFor(hospital.id), trailingIcon: ChevronDown }] : []),
  ];
  const switchRotation = (id) => {
    if (id === MENU_ID) { setPickerOpen(true); return; }
    setActiveRotation(id);
  };

  // ---- tabs ----------------------------------------------------------------
  const tabs = TAB_DEFS;

  const renderTab = () => {
    if (!rotation) return null;

    const d = rotation.data || {};
    if (activeTab === 'overview' && (d.toolSections || d.notesKey)) return <OverviewTab data={d} onAction={runAction} />;
    if (activeTab === 'contacts' && d.contacts) return <ContactList groups={d.contacts.groups} accent={d.accent || accent} storageKey={d.contacts.storageKey} />;
    if (activeTab === 'locations' && d.locations) return <LocationList data={d.locations} />;
    if (activeTab === 'documents' && d.documents) return <DocumentList groups={d.documents.groups} accent={d.accent || accent} storageKey={d.documents.storageKey} onAction={runAction} />;

    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>Content for this section is coming soon.</p>
      </div>
    );
  };

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <HospitalHeader hospital={hospital} subtitle={rotation?.label ? `${rotation.label} rotation` : 'Rotation Resources'} onBack={onBack} />

      {segments.length > 1 && (
        <SegmentedControl
          segments={segments}
          activeSegment={activeInMenu ? MENU_ID : activeRotation}
          onChange={switchRotation}
          hospitalColor={{ from: accent, to: accent }}
        />
      )}
      {pickerOpen && (
        <RotationPicker
          title={menuLabelFor(hospital.id)}
          rotations={menuRotations}
          activeId={activeRotation}
          accent={accent}
          onSelect={(id) => { setActiveRotation(id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-10 shadow-sm">
        <div className="flex px-2">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => { setActiveTab(t.id); }}
                role="tab"
                aria-selected={active}
                className={`flex-1 flex flex-col items-center gap-1 px-2 py-3 border-b-2 transition-colors ${
                  active
                    ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon size={18} />
                <span className="font-semibold text-xs">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-6">{renderTab()}</div>
      <div className="h-8" />
    </div>
  );
};

export default HospitalDetailScreen;
